/**
 * Brand Form Dialog
 *
 * Add a brand, or rename one / replace its logo. Position is deliberately not
 * offered: a new brand lands at the end of the rail and the order is set by
 * dragging, so there is nothing to type in here.
 *
 * A logo is mandatory when adding. Brands with no image render as blank tiles
 * in the app, which is the whole reason the generated placeholder tiles existed
 * — this form is what replaces them.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  LocalOffer as BrandsIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';

import { createBrand, updateBrand } from '../api';
import Logger from '../../../utils/Logger';

/** Mirrors the backend's validate-image middleware, so bad files fail here first. */
const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

/**
 * Structural subset of a brand — deliberately looser than the `Brand` type in
 * `types.ts`, so the dialog works with whatever shape the caller is holding.
 */
export interface EditableBrand {
  id: string;
  name: string;
  imageUrl?: string | null;
}

export interface BrandFormDialogProps {
  open: boolean;
  /** The brand being edited, or null when adding a new one. */
  brand: EditableBrand | null;
  onClose: () => void;
  /** Called after a successful save so the caller can refresh its list. */
  onSaved: () => void;
}

export default function BrandFormDialog({
  open,
  brand,
  onClose,
  onSaved,
}: BrandFormDialogProps): React.ReactElement {
  const isEdit = !!brand;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset whenever the dialog opens so a previous edit never leaks into the next.
  useEffect(() => {
    if (!open) return;
    setName(brand?.name ?? '');
    setFile(null);
    setPreview(brand?.imageUrl ?? null);
    setError(null);
    setSaving(false);
  }, [open, brand]);

  // Revoke the object URL we made for the local preview — but never the brand's
  // own remote imageUrl.
  useEffect(() => {
    if (!file || !preview) return undefined;
    const objectUrl = preview;
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, preview]);

  const handleFile = (picked: File | null): void => {
    if (!picked) return;

    if (!ACCEPTED.includes(picked.type)) {
      setError(`Unsupported file type (${picked.type || 'unknown'}). Use PNG, JPG, WEBP or SVG.`);
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError(`Logo is ${(picked.size / 1024 / 1024).toFixed(2)}MB — the limit is 2MB.`);
      return;
    }

    setError(null);
    setFile(picked);
    setPreview(URL.createObjectURL(picked));
  };

  const handleSave = async (): Promise<void> => {
    const trimmed = name.trim();

    if (!trimmed) {
      setError('Brand name is required');
      return;
    }
    if (!isEdit && !file) {
      setError('A logo is required — a brand with no image shows as a blank tile in the app.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (brand) {
        await updateBrand(brand.id, {
          // Only send what actually changed; an unchanged name is left out.
          name: trimmed !== brand.name ? trimmed : undefined,
          image: file,
        });
      } else {
        await createBrand(trimmed, file as File);
      }

      onSaved();
      onClose();
    } catch (err: unknown) {
      Logger.error('Failed to save brand:', err);
      const body = (err as { body?: { message?: string; error?: string } })?.body;
      setError(body?.message || body?.error || (err as Error)?.message || 'Failed to save brand');
    } finally {
      setSaving(false);
    }
  };

  const nothingToSave = isEdit && !file && name.trim() === (brand?.name ?? '');

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BrandsIcon fontSize="small" />
          <span>{isEdit ? `Edit ${brand?.name}` : 'Add brand'}</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            label="Brand name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            disabled={saving}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 1.5,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1,
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt={name || 'Brand logo'}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              ) : (
                <BrandsIcon sx={{ color: 'action.disabled' }} />
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
              >
                {preview ? 'Replace logo' : 'Choose logo'}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {file ? file.name : 'PNG, JPG, WEBP or SVG · max 2MB'}
              </Typography>
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED.join(',')}
              hidden
              onChange={(e) => {
                handleFile(e.target.files?.[0] ?? null);
                // Clear the input so picking the same file twice still fires onChange.
                e.target.value = '';
              }}
            />
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || nothingToSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isEdit ? 'Save' : 'Add brand'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
